import { PromotionCodeResponse, PromotionPopupData } from '../types/promotion';

/**
 * 할인율을 텍스트로 변환
 * @param discountType 할인 타입
 * @param discountValue 할인 값
 * @returns 할인 텍스트 (예: "10% OFF", "Free")
 */
export const formatDiscountText = (discountType: string, discountValue: number): string => {
  switch (discountType) {
    case 'PERCENTAGE':
      const percentage = Math.round(discountValue * 100);
      // 100% 할인일 때는 "Free"로 표시
      return percentage === 100 ? 'Free' : `${percentage}% OFF`;
    case 'DELIVERY_FEE':
      return 'Free';
    case 'FIXED_AMOUNT':
      return `$${discountValue.toFixed(2)} OFF`;
    default:
      return 'Discount';
  }
};

/**
 * 종료 시간을 포맷팅 (KST 기준)
 * @param endDate 종료 날짜 문자열
 * @returns 포맷팅된 시간 텍스트 (예: "11:30 PM TODAY")
 */
export const formatValidUntilText = (endDate: string): string => {
  const endDateTime = new Date(endDate);
  
  // 현재 시간을 KST로 변환
  const now = new Date();
  const kstOffset = 9 * 60; // 9시간을 분으로 변환
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000); // UTC 시간
  const kstNow = new Date(utc + (kstOffset * 60000)); // KST 시간
  
  // endDate도 KST로 변환 (endDate가 이미 KST라면 그대로, UTC라면 변환)
  const endUtc = endDateTime.getTime() + (endDateTime.getTimezoneOffset() * 60000);
  const kstEndDate = new Date(endUtc + (kstOffset * 60000));
  
  // 시간 포맷팅 (12시간 형식)
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  const timeString = kstEndDate.toLocaleTimeString('en-US', timeOptions);
  
  // KST 기준으로 날짜 비교
  const kstNowDateString = kstNow.toDateString();
  const kstEndDateString = kstEndDate.toDateString();
  const isToday = kstEndDateString === kstNowDateString;
  
  if (isToday) {
    return `${timeString} TODAY`;
  } else {
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric'
    };
    const dateString = kstEndDate.toLocaleDateString('en-US', dateOptions).toUpperCase();
    return `${timeString} ${dateString}`;
  }
};

/**
 * 프로모션이 현재 유효한지 확인
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @returns 유효 여부
 */
export const isPromotionValid = (startDate: string, endDate: string): boolean => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return now >= start && now <= end;
};

/**
 * 현재 시간이 팝업 표시 허용 시간대인지 확인 (오후 1시 ~ 4시 KST)
 * @returns 허용 시간대 여부
 */
export const isWithinPopupDisplayHours = (): boolean => {
  const now = new Date();
  
  // KST 시간대로 변환 (UTC+9)
  const kstOffset = 9 * 60; // 9시간을 분으로 변환
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000); // UTC 시간
  const kstTime = new Date(utc + (kstOffset * 60000)); // KST 시간
  
  const currentHour = kstTime.getHours();
  
  // 오후 1시(13시)부터 4시(16시) 전까지
  return currentHour >= 0 && currentHour < 24;
};

/**
 * 프로모션 팝업 표시 가능 여부 종합 확인
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @param promotionCode 프로모션 코드
 * @returns 표시 가능 여부
 */
export const canShowPromotionPopup = (startDate: string, endDate: string, promotionCode: string): boolean => {
  // 1. 프로모션 기간 내인지 확인
  if (!isPromotionValid(startDate, endDate)) {
    return false;
  }
  
  // 2. 이미 닫혔는지 확인
  if (isPromotionPopupDismissed(promotionCode)) {
    return false;
  }
  
  // 3. 허용 시간대인지 확인
  if (!isWithinPopupDisplayHours()) {
    return false;
  }
  
  return true;
};

/**
 * 프로모션 데이터를 팝업 표시용 데이터로 변환
 * @param promotion 프로모션 API 응답 데이터
 * @returns 팝업 표시용 데이터
 */
export const transformPromotionData = (promotion: PromotionCodeResponse): PromotionPopupData => {
  return {
    code: promotion.code,
    discountText: formatDiscountText(promotion.discountType, promotion.discountValue),
    validUntilText: formatValidUntilText(promotion.endDate),
    isDeliveryFee: promotion.policyType === 'DELIVERY_FEE'
  };
};

/**
 * 로컬 스토리지 키 생성
 * @param promotionCode 프로모션 코드
 * @returns 로컬 스토리지 키
 */
export const getPromotionStorageKey = (promotionCode: string): string => {
  return `promotion-popup-dismissed-${promotionCode}`;
};

/**
 * 프로모션 팝업이 이미 닫혔는지 확인
 * @param promotionCode 프로모션 코드
 * @returns 이미 닫혔는지 여부
 */
export const isPromotionPopupDismissed = (promotionCode: string): boolean => {
  const key = getPromotionStorageKey(promotionCode);
  return localStorage.getItem(key) === 'true';
};

/**
 * 프로모션 팝업을 닫음으로 표시
 * @param promotionCode 프로모션 코드
 */
export const dismissPromotionPopup = (promotionCode: string): void => {
  const key = getPromotionStorageKey(promotionCode);
  localStorage.setItem(key, 'true');
}; 